/**
 * Time-of-day window for reports — the "only alerts between 8 AM and 8 PM" filter.
 *
 * Stored and transported as **minutes from midnight, 0-1440**, half-open: an event is
 * in the window when `start <= minuteOfDay < end`. Two consequences worth knowing:
 *
 *   - "8 AM to 8 PM" is [480, 1200) and ends at 19:59:59, not 20:59:59. Inclusive
 *     hour bounds were rejected for exactly that off-by-one.
 *   - All day is [0, 1440), which every minute of the day satisfies, so the default
 *     is a genuine no-op rather than a special case the query has to test for.
 *
 * Minutes rather than hours because the SQL already handles any granularity; offering
 * half-hours or quarter-hours later is a change to the option lists below and nothing
 * else.
 *
 * A window may cross midnight (start > end), which is how night-shift reporting works.
 * It is a MASK over each day in the report's date range, not a shift: "Last Week,
 * 8 PM - 8 AM" begins at 00:00 on the Monday, not 20:00 the Sunday before. See
 * docs/REPORT_TIME_WINDOW_PLAN.md for why.
 */

export const DAY_MINUTES = 1440;

/** All day. Anything equal to this must produce today's unfiltered behaviour. */
export const DEFAULT_TIME_WINDOW = { start: 0, end: DAY_MINUTES };

/**
 * Is this window the whole day?
 *
 * True for the default, and also when start equals end — a degenerate window that
 * could mean "zero minutes", but returning no rows for a mis-set control is a worse
 * experience than returning all of them, so it is treated as all day here and in SQL.
 */
export function isAllDayWindow(start, end) {
  const s = Number(start);
  const e = Number(end);
  if (!Number.isFinite(s) || !Number.isFinite(e)) return true;
  if (s === e) return true;
  return s === 0 && e === DAY_MINUTES;
}

/** Does the window run past midnight into the next day? */
export function crossesMidnight(start, end) {
  if (isAllDayWindow(start, end)) return false;
  return Number(start) > Number(end);
}

/**
 * Coerce anything into a usable {start, end}, falling back to all day.
 *
 * Accepts the object form the UI holds and the array form the wire uses, so callers
 * do not each need their own guard. Values are clamped rather than rejected: a stored
 * window from a future build with finer granularity should degrade, not error.
 */
export function normalizeTimeWindow(win) {
  let s;
  let e;
  if (Array.isArray(win)) {
    [s, e] = win;
  } else if (win && typeof win === 'object') {
    s = win.start;
    e = win.end;
  }
  s = Math.round(Number(s));
  e = Math.round(Number(e));
  if (!Number.isFinite(s) || !Number.isFinite(e)) return { ...DEFAULT_TIME_WINDOW };
  s = Math.min(Math.max(s, 0), DAY_MINUTES);
  e = Math.min(Math.max(e, 0), DAY_MINUTES);
  return { start: s, end: e };
}

/** Minutes from midnight as a wire-format pair. */
export function timeWindowToValues(win) {
  const { start, end } = normalizeTimeWindow(win);
  return [start, end];
}

/**
 * "8:00 AM", "12:30 PM", "Midnight".
 *
 * Both 0 and 1440 render as "Midnight": in a start control it reads as the beginning
 * of the day and in an end control as the close of it, which is what a reader expects.
 * "12:00 AM" for the end of the day would look like a mistake.
 */
export function minutesToClockLabel(minutes) {
  const m = Math.round(Number(minutes));
  if (!Number.isFinite(m)) return '';
  if (m <= 0 || m >= DAY_MINUTES) return 'Midnight';
  const hour24 = Math.floor(m / 60);
  const mins = m % 60;
  const suffix = hour24 < 12 ? 'AM' : 'PM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${String(mins).padStart(2, '0')} ${suffix}`;
}

/**
 * The window as one phrase, or '' when it is the whole day.
 *
 * Empty for all day on purpose: it feeds `labels` in report_filter_terms, and a
 * schedule with no window should show nothing rather than "Midnight - Midnight".
 * Shared by the page, the schedule drawer and the schedules list so a window cannot
 * be worded three ways.
 */
export function describeTimeWindow(start, end) {
  if (isAllDayWindow(start, end)) return '';
  const phrase = `${minutesToClockLabel(start)} – ${minutesToClockLabel(end)}`;
  return crossesMidnight(start, end) ? `${phrase} (overnight)` : phrase;
}

/**
 * Selectable boundaries, on the hour.
 *
 * Start stops at 23:00 and end starts at 01:00 so neither control can be pushed to a
 * value that has no meaning on its own side. Equal values are still reachable, and
 * mean all day.
 */
export function timeWindowStartOptions(stepMinutes = 60) {
  const out = [];
  for (let m = 0; m < DAY_MINUTES; m += stepMinutes) {
    out.push({ value: m, label: minutesToClockLabel(m) });
  }
  return out;
}

export function timeWindowEndOptions(stepMinutes = 60) {
  const out = [];
  for (let m = stepMinutes; m <= DAY_MINUTES; m += stepMinutes) {
    out.push({ value: m, label: minutesToClockLabel(m) });
  }
  return out;
}
